import { observer } from 'mobx-react'
import { useEffect, useState } from 'react'
import { useStore } from '../../stores'

import style from './Input.module.css'
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete'
import LinearProgress from 'App/LinearProgress'
import Tag, { type TagItem } from 'App/Tag'
import { TextField } from '@mui/material'
import { useOutSideClickListener } from 'helpers'

export interface InputProps {
  onChange: (value: string[] | undefined) => void
  value?: string[]
}

const TEXTFIELD_STYLES = {
  borderColor: 'lightgray',
  borderWidth: 1
}

const NEW_CATEGORY_HINT = '(Neue Kategorie)'

const filter = createFilterOptions<string>()

const TagAutoComplete = observer(
  ({
    tagsInProject,
    selectedTags,
    setSelectedTags,
    onChange,
    setIsDropdownOpen
  }) => {
    const [value, setValue] = useState<string>('')

    const isNewTag = (value: string): boolean => {
      const tagExistsInProject: boolean = tagsInProject.some(
        (tag: TagItem) => tag.name === value
      )
      const tagExistInSelectedValues: boolean = selectedTags.some(
        (tag: TagItem) => tag.name === value
      )
      return !tagExistInSelectedValues && !tagExistsInProject
    }
    return (
      <Autocomplete
        id="filter-complete"
        freeSolo={true}
        onInputChange={(event, value) => {
          setValue(value)
        }}
        filterOptions={(options, params) => {
          const filtered = filter(options as string[], params).filter(
            (option) => {
              const optionInSelectedTags: boolean = selectedTags
                .map((tag: TagItem) => tag.name)
                .includes(option)
              return !optionInSelectedTags
            }
          )
          const { inputValue } = params
          if (inputValue.trim().length > 0 && isNewTag(inputValue)) {
            filtered.push(`${inputValue} ${NEW_CATEGORY_HINT}`)
          }
          return filtered
        }}
        onChange={(event, val: string) => {
          if (val.trim().length > 0) {
            let currentTags: TagItem[] = selectedTags
            let tag: TagItem = tagsInProject.find(
              (tag: TagItem) => tag.name === val
            )
            if (tag === null || tag === undefined) {
              tag = {
                uuid: 'new',
                name: val.endsWith(NEW_CATEGORY_HINT) ? value : val
              }
            }

            const currentTagsIncludeTag: boolean = currentTags.some(
              (item: TagItem) => item.name === tag.name
            )
            if (!currentTagsIncludeTag) {
              currentTags = [...selectedTags, tag]
              setSelectedTags(currentTags)
              onChange(currentTags.map((tag: TagItem) => tag.name))
            }
          }
          setValue('')
          setIsDropdownOpen(false)
        }}
        options={tagsInProject
          .filter((tag: TagItem) => {
            const selectedTagsIncludeTag: boolean = selectedTags.includes(tag)
            return !selectedTagsIncludeTag
          })
          .map((tag: TagItem) => tag.name)}
        renderOption={(props, option) => <li {...props}>{option}</li>}
        renderInput={(params) => {
          params.inputProps.maxLength = 100
          return (
            <TextField
              {...params}
              label="Neue Kategorie erstellen"
              variant="outlined"
              InputLabelProps={{
                style: { color: '#2f4573' }
              }}
            />
          )
        }}
        sx={{
          m: 2,
          '& .MuiOutlinedInput-root': {
            '& fieldset': TEXTFIELD_STYLES,
            '&:hover fieldset': TEXTFIELD_STYLES,
            '&.Mui-focused fieldset': TEXTFIELD_STYLES
          },
          '& .MuiAutocomplete-inputFocused': {
            '--tw-ring-color': 'transparent'
          }
        }}
      />
    )
  }
)

const TagInput = observer((props: InputProps) => {
  const { app: appStore } = useStore()
  const [selectedTags, setSelectedTags] = useState<TagItem[]>([])
  const [tagsInProject, setTagsInProject] = useState<TagItem[]>([])
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  // Fetch available tags for project
  useEffect(() => {
    setIsLoading(true)
    void appStore.selectedProject
      .client(`/tag/`)
      .then((res) => {
        const tagList: TagItem[] = res.data
        setTagsInProject(tagList)
        setSelectedTags(
          tagList.filter((tag) => (props.value ?? []).includes(tag.name))
        )
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [])

  const handleTagDelete = (tagToDelete: TagItem): void => {
    const newSelectedTags = selectedTags.filter(
      (selectedTag) => selectedTag !== tagToDelete
    )
    setSelectedTags(newSelectedTags)
    props.onChange(newSelectedTags.map((tag) => tag.name))
  }

  // open /close DropDown
  const handleInputClick = (): void => {
    setIsDropdownOpen(!isDropdownOpen)
  }

  const tagComponentRef = useOutSideClickListener<HTMLDivElement>(() => {
    setIsDropdownOpen(false)
  })

  return (
    <div
      className={[
        style.outerbox,
        isDropdownOpen ? style.outerboxHighlight : ''
      ].join(' ')}
      ref={tagComponentRef}
    >
      {isLoading ? (
        <LinearProgress />
      ) : (
        <div
          onClick={handleInputClick}
          className={'px-2 py-2 flex flex-wrap gap-1 overflow-y-auto max-h-24'}
        >
          {selectedTags?.map((tag, index) => (
            <Tag
              key={index}
              label={tag.name}
              backgroundColor={tag.color}
              handleDelete={() => {
                handleTagDelete(tag)
              }}
            />
          ))}
          {(selectedTags === undefined || selectedTags.length === 0) &&
            'Kein Wert'}
        </div>
      )}
      {isDropdownOpen && (
        <TagAutoComplete
          tagsInProject={tagsInProject}
          selectedTags={selectedTags}
          setSelectedTags={setSelectedTags}
          onChange={props.onChange}
          setIsDropdownOpen={setIsDropdownOpen}
        />
      )}
    </div>
  )
})

export default TagInput
